import { createSlice } from '@reduxjs/toolkit';

interface ShowState {
    shows: any[];
    showsListParams: {
        total: number;
        page: number;
        per_page: number;
    };
    currentShow: any | null;
    loading: boolean;
    availability: any | null;
    jobStatus: {
        jobId: string | null;
        status: string | null;
        progress: number;
        error: string | null;
    };
    adminLoading: boolean;
    syncingShowId: string | null;
}

const initialState: ShowState = {
    shows: [],
    showsListParams: {
        total: 0,
        page: 1,
        per_page: 10
    },
    currentShow: null,
    availability: null,
    loading: false,
    jobStatus: {
        jobId: null,
        status: null,
        progress: 0,
        error: null
    },
    adminLoading: false,
    syncingShowId: null,
};

const showSlice = createSlice({
    name: 'show',
    initialState,
    reducers: {
        getShowsByDateRange: (state) => {
            state.loading = true;
        },
        getShowsByDateRangeSuccess: (state, action) => {
            const { data, ...params } = action.payload;
            state.showsListParams = params;
            state.shows = data;
            state.loading = false;
        },
        getShowsByDateRangeFailed: (state) => {
            state.loading = false;
        },
        getShowById: (state) => {
            state.loading = true;
        },
        getShowByIdSuccess: (state, action) => {
            state.currentShow = action.payload;
            state.loading = false;
        },
        getShowByIdFailed: (state) => {
            state.loading = false;
        },
        createShow: (state) => {
            state.loading = true;
        },
        createShowSuccess: (state, action) => {
            state.shows = [action.payload, ...state.shows];
            state.loading = false;
        },
        createShowFailed: (state) => {
            state.loading = false;
        },
        updateShow: (state) => {
            state.loading = true;
        },
        updateShowSuccess: (state, action) => {
            const index = state.shows.findIndex(show => show._id === action.payload._id);
            if (index !== -1) {
                state.shows[index] = action.payload;
            }
            state.loading = false;
        },
        updateShowFailed: (state) => {
            state.loading = false;
        },
        deleteShow: (state) => {
            state.loading = true;
        },
        deleteShowSuccess: (state, action) => {
            state.shows = state.shows.filter(show => show._id !== action.payload);
            state.loading = false;
        },
        deleteShowFailed: (state) => {
            state.loading = false;
        },
        createMonthlyDefaultShows: (state) => {
            state.loading = true;
        },
        createMonthlyDefaultShowsSuccess: (state, action) => {
            state.shows = action.payload;
            state.loading = false;
        },
        createMonthlyDefaultShowsFailed: (state) => {
            state.loading = false;
        },
        updateGlobalMaxCapacity: (state) => {
            state.loading = true;
        },
        updateGlobalMaxCapacitySuccess: (state) => {
            state.loading = false;
        },
        updateGlobalMaxCapacityFailed: (state) => {
            state.loading = false;
        },
        initializeHistoricalShows: (state) => {
            state.loading = true;
            state.jobStatus = {
                jobId: null,
                status: null,
                progress: 0,
                error: null
            };
        },
        initializeHistoricalShowsSuccess: (state, action) => {
            state.loading = false;
            state.jobStatus.jobId = action.payload.jobId;
        },
        initializeHistoricalShowsFailed: (state, action) => {
            state.loading = false;
            state.jobStatus.error = action.payload;
        },
        resetAvailability: (state) => {
            state.adminLoading = true;
        },
        resetAvailabilitySuccess: (state) => {
            state.adminLoading = false;
        },
        resetAvailabilityFailed: (state) => {
            state.adminLoading = false;
        },
        updateJobStatus: (state, action) => {
            state.jobStatus = {
                ...state.jobStatus,
                ...action.payload
            };
        },
        recalibrateTimes: (state) => {
            state.adminLoading = true;
        },
        recalibrateTimesSuccess: (state) => {
            state.adminLoading = false;
        },
        recalibrateTimesFailed: (state) => {
            state.adminLoading = false;
        },
        syncShowCapacity: (state) => {
            state.adminLoading = true;
        },
        syncShowCapacitySuccess: (state) => {
            state.adminLoading = false;
        },
        syncShowCapacityFailed: (state) => {
            state.adminLoading = false;
        },
        syncShowCapacityById: (state, action) => {
            state.syncingShowId = action.payload;
        },
        syncShowCapacityByIdSuccess: (state, action) => {
            const index = state.shows.findIndex(show => show._id === action.payload._id);
            if (index !== -1) {
                state.shows[index] = action.payload;
            }
            state.syncingShowId = null;
        },
        syncShowCapacityByIdFailed: (state) => {
            state.syncingShowId = null;
        },
    },
});

export const {
    getShowsByDateRange,
    getShowsByDateRangeSuccess,
    getShowsByDateRangeFailed,
    getShowById,
    getShowByIdSuccess,
    getShowByIdFailed,
    createShow,
    createShowSuccess,
    createShowFailed,
    updateShow,
    updateShowSuccess,
    updateShowFailed,
    deleteShow,
    deleteShowSuccess,
    deleteShowFailed,
    createMonthlyDefaultShows,
    createMonthlyDefaultShowsSuccess,
    createMonthlyDefaultShowsFailed,
    updateGlobalMaxCapacity,
    updateGlobalMaxCapacitySuccess,
    updateGlobalMaxCapacityFailed,
    initializeHistoricalShows,
    initializeHistoricalShowsSuccess,
    initializeHistoricalShowsFailed,
    resetAvailability,
    resetAvailabilitySuccess,
    resetAvailabilityFailed,
    updateJobStatus,
    recalibrateTimes,
    recalibrateTimesSuccess,
    recalibrateTimesFailed,
    syncShowCapacity,
    syncShowCapacitySuccess,
    syncShowCapacityFailed,
    syncShowCapacityById,
    syncShowCapacityByIdSuccess,
    syncShowCapacityByIdFailed,
} = showSlice.actions;

export default showSlice.reducer; 